/**
 * Hook dédié aux calculs pricing de la Navigation.
 *
 * Extrait de Navigation.tsx pour réduire la taille du composant
 * et rendre les calculs testables indépendamment.
 */

import { useMemo, useEffect } from 'react';
import { calculateMissionAnnualCost, calculateAnnualCostYears } from '@/utils/synthesisCalculations';
import {
  PricingMode,
  ComparisonModel,
  PACK_AILIX_CONSTANTS,
  calculatePackAilixEligibility,
  getTjmForMode,
  getDiscountsForMode,
  calculateTotalCostForMode,
  getComparisonModel,
  calculateHybridTotalCostForMode,
} from '@/utils/packAilixPricing';
import { ClientData, Mission } from '@/services/clientDataService';
import { calculateComplementaryOptionsCost } from '@/utils/optionUtils';
import { buildFlatMissionMap, deduplicateSelectedMissionItems } from '@/utils/subMissionUtils';
import { computeGroupDiscounts } from '@/utils/groupDiscountPricing';

// ============================================================================
// TYPES
// ============================================================================

interface NavigationPricingParams {
  selectedItems: Array<{ id: string; type: 'mission' | 'option'; quantity: number }>;
  allMissions: Array<{ id: string; title: string; duration?: string; duration_days?: number; price?: number }>;
  dynamicOptions: Array<{ id: string; price: number; is_included: boolean; option_id?: string }>;
  propsPricing: { total: number; discounts: Array<{ name: string; amount: number }> } | undefined;
  clientData: ClientData | null;
  pricingMode: PricingMode;
  setPricingMode: React.Dispatch<React.SetStateAction<PricingMode>>;
  pricingNbDevelopers?: number;
  enabledPrestations?: Record<string, boolean>;
}

export interface NavigationPricingResult {
  pricingMode: PricingMode;
  setPricingMode: React.Dispatch<React.SetStateAction<PricingMode>>;
  pricing: { total: number; discounts: Array<{ name: string; amount: number }> } | null | undefined;
  totalDays: number;
  packAilixEligibility: ReturnType<typeof calculatePackAilixEligibility>;
  calculatedEconomies: { annualSavings: number; savings3Years: number };
  totalEtpSaved: number;
  annualCosts: AnnualCostsResult | null;
  includedOptionsCost: number;
  /** Modèle de comparaison actif (legacy ou hybrid) */
  comparisonModel: ComparisonModel;
  /** Prix réel propsPricing (debug/QA, toujours disponible) */
  pricingActual: { total: number; discounts: Array<{ name: string; amount: number }> } | null | undefined;
}

export interface AnnualCostsResult {
  totalAnnualCost: number;
  totalAnnualCostWithTemporality: number;
  totalCostWithProjectDuration: number;
  projectDurationYears: number;
}

// ============================================================================
// HOOK
// ============================================================================

export function useNavigationPricing({
  selectedItems,
  allMissions,
  dynamicOptions,
  propsPricing,
  clientData,
  pricingMode,
  setPricingMode,
  pricingNbDevelopers,
  enabledPrestations,
}: NavigationPricingParams): NavigationPricingResult {
  // Force a_la_carte si pack_ailix_enabled n'est pas activé pour ce client
  useEffect(() => {
    if (clientData?.pack_ailix_enabled !== true && pricingMode !== 'a_la_carte') {
      setPricingMode('a_la_carte');
    }
  }, [clientData?.pack_ailix_enabled, pricingMode, setPricingMode]);

  // Flat map pour résoudre les sous-missions par ID
  const missionMap = useMemo(() => buildFlatMissionMap(allMissions ?? []), [allMissions]);

  // Déduplication : retirer les parents groupe si leurs sous-missions sont aussi sélectionnées
  const deduplicatedItems = useMemo(
    () => deduplicateSelectedMissionItems(selectedItems ?? [], allMissions ?? []),
    [selectedItems, allMissions]
  );

  // --- Total jours des missions sélectionnées ---
  const totalDays = useMemo(() => {
    if (!Array.isArray(deduplicatedItems)) return 0;
    return deduplicatedItems
      .filter(item => item?.type === 'mission')
      .reduce((sum, item) => {
        const mission = missionMap.get(item?.id ?? '');
        if (!mission) return sum;
        const days = mission.duration_days || parseFloat((mission.duration?.split(' ')[0] || '0').replace(',', '.')) || 0;
        return sum + (days * (item?.quantity || 1));
      }, 0);
  }, [deduplicatedItems, missionMap]);

  // --- Éligibilité Pack Ailix ---
  const packAilixEligibility = useMemo(
    () => calculatePackAilixEligibility(totalDays),
    [totalDays]
  );

  // Reset si non éligible
  useEffect(() => {
    if (!packAilixEligibility.eligible && pricingMode !== 'a_la_carte') {
      setPricingMode('a_la_carte');
    }
  }, [packAilixEligibility.eligible, pricingMode, setPricingMode]);

  // --- Options obligatoires coût ---
  const includedOptionsCost = useMemo(() => {
    if (!Array.isArray(dynamicOptions)) return 0;
    return dynamicOptions
      .filter(opt => opt.is_included === true)
      .reduce((sum, opt) => sum + (opt.price || 0), 0);
  }, [dynamicOptions]);

  // --- Modèle de comparaison (legacy ou hybrid) ---
  const packConfig = clientData?.pack_ailix_config ?? null;
  const comparisonModel = getComparisonModel(packConfig);

  // --- Calcul des options complémentaires (non-core) ---
  // Source unique via optionUtils.calculateComplementaryOptionsCost
  const complementaryOptionsCost = useMemo(
    () => calculateComplementaryOptionsCost(dynamicOptions, deduplicatedItems),
    [dynamicOptions, deduplicatedItems],
  );

  // --- Remise groupe pré-calculée pour moteur hybride/pack ---
  const groupDiscountAmount = useMemo(() => {
    const items = deduplicatedItems
      .filter((i) => i?.type === 'mission')
      .map((i) => ({ id: i.id, quantity: i.quantity || 1 }));
    if (items.length === 0 || !Array.isArray(allMissions)) return 0;
    const clientTjm = clientData?.tjm ?? undefined;
    const tjm = getTjmForMode(pricingMode, packConfig, clientTjm);
    const priceResolver = (sub: Mission) => (sub.duration_days || 0) * tjm;
    const discounts = computeGroupDiscounts(allMissions as Mission[], items, priceResolver);
    return discounts.reduce((sum, d) => sum + d.amount, 0);
  }, [deduplicatedItems, allMissions, pricingMode, packConfig, clientData?.tjm]);

  // --- Pricing dynamique selon mode ---
  const dynamicPricing = useMemo(() => {
    if (!propsPricing || !Array.isArray(deduplicatedItems)) return propsPricing ?? null;
    const hasMissions = deduplicatedItems.some(item => item?.type === 'mission');
    if (!hasMissions) return propsPricing;

    const nbDevs = typeof pricingNbDevelopers === 'number' && pricingNbDevelopers > 0
      ? pricingNbDevelopers
      : 2;
    const clientTjm = clientData?.tjm ?? undefined;
    const clientTjmMoe = clientData?.tjm_moe ?? undefined;
    const disabledVolumeDiscounts = clientData?.disabled_volume_discounts ?? undefined;

    // --- Mode hybride : recalcul pour TOUS les modes via le moteur unifié ---
    if (comparisonModel === 'hybrid' && packAilixEligibility.eligible) {
      const exceptionalDiscountPct = clientData?.exceptional_discount_percentage ?? undefined;
      const result = calculateHybridTotalCostForMode(
        totalDays, nbDevs, pricingMode,
        packConfig, clientTjm, clientTjmMoe,
        enabledPrestations,
        disabledVolumeDiscounts,
        complementaryOptionsCost,
        exceptionalDiscountPct,
        groupDiscountAmount,
      );
      return {
        total: result.total,
        discounts: result.discounts.map(d => ({ name: d.name, amount: d.amount })),
      };
    }

    // --- Mode legacy ---
    // À la carte : utiliser propsPricing tel quel (source de vérité depuis useProjectPricing)
    if (pricingMode === 'a_la_carte') {
      return propsPricing;
    }

    if (!packAilixEligibility.eligible) return propsPricing;

    // Pack legacy : même base que À la carte, seul le coût dev (jours × TJM) change.
    // Les options et remises restent identiques.
    const aLaCarteTjm = getTjmForMode('a_la_carte', packConfig, clientTjm);
    const packTjm = getTjmForMode(pricingMode, packConfig, clientTjm);
    const tjmSavings = totalDays * (aLaCarteTjm - packTjm);
    const packTotal = (propsPricing?.total ?? 0) - tjmSavings;

    return {
      total: packTotal,
      discounts: [
        ...(propsPricing?.discounts ?? []),
        { name: `Économie ${pricingMode === 'pack_ailix_1an' ? 'Pack 1 an' : 'Pack 2+ ans'} (TJM ${packTjm}€ vs ${aLaCarteTjm}€)`, amount: tjmSavings },
      ],
    };
  }, [
    propsPricing,
    pricingMode,
    deduplicatedItems,
    packAilixEligibility.eligible,
    totalDays,
    dynamicOptions,
    clientData,
    comparisonModel,
    complementaryOptionsCost,
    packConfig,
    pricingNbDevelopers,
    enabledPrestations,
    groupDiscountAmount,
  ]);

  const pricing = dynamicPricing;

  // --- Économies annuelles ---
  const calculatedEconomies = useMemo(() => {
    if (!Array.isArray(deduplicatedItems) || !Array.isArray(allMissions)) {
      return { annualSavings: 0, savings3Years: 0 };
    }
    const annualSavings = deduplicatedItems
      .filter(item => item?.type === 'mission')
      .reduce((sum, item) => {
        const mission = missionMap.get(item?.id ?? '');
        if (!mission) return sum;
        const qty = item?.quantity || 1;
        const missionAny = mission as any;
        const roiData = missionAny.roi_data || missionAny.complete_json?.roi_data || {};
        const hourlyRate = 44;
        const workingHoursPerDay = 7;
        if (roiData.annual_saving_eur) return sum + roiData.annual_saving_eur * qty;
        if (roiData.savings?.annual) return sum + roiData.savings.annual * qty;
        const durationDays = parseFloat((mission.duration?.split(' ')[0] || '10').replace(',', '.'));
        return sum + durationDays * hourlyRate * workingHoursPerDay * qty;
      }, 0);
    return { annualSavings: Math.round(annualSavings), savings3Years: Math.round(annualSavings * 3) };
  }, [deduplicatedItems, allMissions]);

  // --- ETP économisés ---
  const totalEtpSaved = useMemo(() => {
    if (!Array.isArray(deduplicatedItems) || !Array.isArray(allMissions)) return 0;
    return deduplicatedItems
      .filter(item => item?.type === 'mission')
      .reduce((sum, item) => {
        const mission = missionMap.get(item?.id ?? '');
        if (!mission) return sum;
        const missionAny = mission as any;
        const roiData = missionAny?.roi_data || missionAny?.complete_json?.roi_data || {};
        const etpSaved = roiData?.etp_economise ?? roiData?.etp_saved ?? roiData?.savings?.etp ?? 0;
        return sum + (etpSaved * (item?.quantity || 1));
      }, 0);
  }, [deduplicatedItems, allMissions]);

  // --- Coûts annuels avec temporalité (calculé UNE seule fois, plus de IIFE dupliquée) ---
  const annualCosts = useMemo((): AnnualCostsResult | null => {
    if (!Array.isArray(deduplicatedItems) || !Array.isArray(allMissions) || !pricing) return null;
    const selectedMissionItems = deduplicatedItems.filter(item => item?.type === 'mission' && item?.id);
    if (selectedMissionItems.length === 0) return null;

    const projectDurationYears = clientData?.project_duration_years || 3;
    const annualCostPercentage = clientData?.annual_cost_percentage || 20.0;
    let totalAnnualCostWithTemporality = 0;
    let totalAnnualCost = 0;

    for (const item of selectedMissionItems) {
      const mission = missionMap.get(item.id);
      if (!mission) continue;
      const qty = item.quantity || 1;
      const ac = calculateMissionAnnualCost(mission as any, annualCostPercentage);
      const years = calculateAnnualCostYears(mission as any, projectDurationYears);
      totalAnnualCost += ac * qty;
      totalAnnualCostWithTemporality += ac * years * qty;
    }

    return {
      totalAnnualCost,
      totalAnnualCostWithTemporality,
      totalCostWithProjectDuration: pricing.total + totalAnnualCostWithTemporality,
      projectDurationYears,
    };
  }, [deduplicatedItems, allMissions, pricing, clientData?.project_duration_years, clientData?.annual_cost_percentage]);

  return {
    pricingMode,
    setPricingMode,
    pricing,
    totalDays,
    packAilixEligibility,
    calculatedEconomies,
    totalEtpSaved,
    annualCosts,
    includedOptionsCost,
    comparisonModel,
    pricingActual: propsPricing ?? null,
  };
}
